import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AdminDashboardPage.css';

const API_BASE_URL = 'https://mellowmellow.io/api';

const AdminDashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [content, setContent] = useState([]);
  const [helpMessages, setHelpMessages] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const config = {
        headers: { 'x-auth-token': token }
      };

      // Load statistics
      const statsResponse = await axios.get(`${API_BASE_URL}/admin/statistics`, config);
      setStatistics(statsResponse.data);

      // Load users
      const usersResponse = await axios.get(`${API_BASE_URL}/admin/users?page=${currentPage}&search=${searchTerm}`, config);
      setUsers(usersResponse.data.users);
      setTotalPages(usersResponse.data.pagination.totalPages);

      // Load content
      const contentResponse = await axios.get(`${API_BASE_URL}/admin/content`, config);
      setContent(contentResponse.data);

      // Load help messages
      const helpResponse = await axios.get(`${API_BASE_URL}/admin/help-messages?status=pending`, config);
      setHelpMessages(helpResponse.data.messages);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, searchTerm]);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
      navigate('/dashboard');
      return;
    }
    loadDashboardData();
  }, [user, navigate, loadDashboardData]);

  const handleUserStatusToggle = async (userId) => {
    try {
      const config = {
        headers: { 'x-auth-token': token }
      };
      await axios.put(`${API_BASE_URL}/admin/users/${userId}/toggle-status`, {}, config);
      loadDashboardData();
    } catch (error) {
      console.error('Error toggling user status:', error);
    }
  };

  const handleUserRoleUpdate = async (userId, newRole) => {
    try {
      const config = {
        headers: { 'x-auth-token': token }
      };
      await axios.put(`${API_BASE_URL}/admin/users/${userId}/role`, { role: newRole }, config);
      loadDashboardData();
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const config = {
          headers: { 'x-auth-token': token }
        };
        await axios.delete(`${API_BASE_URL}/admin/users/${userId}`, config);
        loadDashboardData();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const renderDashboard = () => (
    <div className="dashboard-stats">
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Users</h3>
          <p className="stat-number">{statistics.totalUsers || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Active Users</h3>
          <p className="stat-number">{statistics.activeUsers || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Total Logins</h3>
          <p className="stat-number">{statistics.totalLogins || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Pending Help</h3>
          <p className="stat-number">{statistics.pendingHelpMessages || 0}</p>
        </div>
      </div>
      
      {statistics.userStats && (
        <div className="user-stats">
          <h3>User Distribution</h3>
          <div className="stats-bars">
            {statistics.userStats.map((stat, index) => (
              <div key={index} className="stat-bar">
                <span className="stat-label">{stat._id}</span>
                <div className="stat-bar-container">
                  <div 
                    className="stat-bar-fill" 
                    style={{ width: `${(stat.count / statistics.totalUsers) * 100}%` }}
                  ></div>
                </div>
                <span className="stat-count">{stat.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderUserManagement = () => (
    <div className="user-management">
      <div className="search-section">
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <button onClick={handleSearch}>Search</button>
      </div>

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id}>
                <td>{user.username}</td>
                <td>{user.email}</td>
                <td>
                  {user.role === 'superadmin' ? (
                    <span className="role-badge superadmin">{user.role}</span>
                  ) : user.role === 'admin' ? (
                    <span className="role-badge admin">{user.role}</span>
                  ) : (
                    <span className="role-badge user">{user.role}</span>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}</td>
                <td>
                  <div className="action-buttons">
                    {user.role !== 'superadmin' && (
                      <button
                        onClick={() => handleUserStatusToggle(user._id)}
                        className={`btn-toggle ${user.isActive ? 'btn-deactivate' : 'btn-activate'}`}
                      >
                        {user.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                    {user.role !== 'superadmin' && (
                      <select
                        value={user.role}
                        onChange={(e) => handleUserRoleUpdate(user._id, e.target.value)}
                        className="role-select"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        {user.role === 'superadmin' && <option value="superadmin">Superadmin</option>}
                      </select>
                    )}
                    {user.role !== 'superadmin' && (
                      <button
                        onClick={() => handleDeleteUser(user._id)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderContentManagement = () => (
    <div className="content-management">
      <h3>Content Management</h3>
      <div className="content-grid">
        {content.map((item) => (
          <div key={item._id} className="content-card">
            <h4>{item.title}</h4>
            <p className="content-type">{item.type}</p>
            <p className="content-preview">{item.content.substring(0, 100)}...</p>
            <div className="content-meta">
              <span>Created: {new Date(item.createdAt).toLocaleDateString()}</span>
              {item.updatedAt && (
                <span>Updated: {new Date(item.updatedAt).toLocaleDateString()}</span>
              )}
            </div>
            <button className="btn-edit">Edit Content</button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderHelpMessages = () => (
    <div className="help-messages">
      <h3>Help Messages</h3>
      <div className="messages-list">
        {helpMessages.map((message) => (
          <div key={message._id} className="message-card">
            <div className="message-header">
              <h4>{message.subject}</h4>
              <span className={`status-badge ${message.status}`}>{message.status}</span>
            </div>
            <p className="message-content">{message.message}</p>
            <div className="message-meta">
              <span>From: {message.username}</span>
              <span>Date: {new Date(message.createdAt).toLocaleDateString()}</span>
            </div>
            {message.adminResponse && (
              <div className="admin-response">
                <h5>Admin Response:</h5>
                <p>{message.adminResponse}</p>
              </div>
            )}
            <button className="btn-respond">Respond</button>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Welcome, {user?.username} ({user?.role})</p>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          User Management
        </button>
        <button
          className={`tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          Content Management
        </button>
        <button
          className={`tab ${activeTab === 'help' ? 'active' : ''}`}
          onClick={() => setActiveTab('help')}
        >
          Help Messages
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'users' && renderUserManagement()}
        {activeTab === 'content' && renderContentManagement()}
        {activeTab === 'help' && renderHelpMessages()}
      </div>
    </div>
  );
};

export default AdminDashboardPage; 
